// Adding and removing a timesheet line — the payload half, kept pure.
//
// ── Why this exists ────────────────────────────────────────────────────────
//
// The rebuilt SheetDetail could EDIT a line's hours and nothing else. A crew
// member who forgets to clock in on a job has no clock record, so "Add from
// clock" finds nothing, and there was no other way to put those hours on the
// sheet — the worker simply was not paid for the job. That is the whole of the
// "TIMESHEETS ARE FUCKED" complaint, and it needed no new endpoint: the one
// the hours edit already uses does both.
//
// ── The server contract, read off the source ───────────────────────────────
//
// PUT /api/timesheets/:id  (server/routes/timesheets.js:497) validates with
// hoursModel.validateLines (:513) then calls hoursModel.applyLineEdits (:521).
// In server/utils/hoursModel.js:
//
//   :181  an entry WITH an `id` the sheet already has  -> UPDATE
//   :203  an entry WITHOUT an `id`                     -> INSERT
//   :224  every current line NOT in the posted array   -> DELETE
//         (`keep` collects the ids that were sent; the loop deletes the rest)
//
// So there is no add endpoint and no delete endpoint. "Add" is the full array
// plus one id-less object; "remove" is the full array minus one. Inventing a
// second way for a line to reach the database is how provenance gets lost.
//
// validateLines (:238) refuses two things, and both are checked here BEFORE
// the request so the screen never offers a button the server answers 400 to:
// an archived job, and the same (job, date) pair twice.
//
// ── An HOURS line sends no money field. CLAUDE.md rule 4 ───────────────────
//
// It carries job, date, hours and notes. Nothing else. The server then writes
// pay_type 'hourly' and pay_amount NULL (hoursModel.js:213) — exactly what
// buildOrRefreshFromClock writes for a line built from the clock (:134) — and
// paystubCalc.resolveEntryPay takes BOTH the type and the amount from the JOB
// when the entry carries no amount of its own (paystubCalc.js:107-111). So a
// line added here is paid precisely as the same line would have been had the
// worker remembered to clock in.
//
// ── An AMOUNT line is the one exception, and it is why one exists ──────────
//
// Added 2026-09-10. The owner: "The add a line by hand looks good, but
// doesn't let me add a set cost. What if I want to adjust the timesheet to
// show reinbursement, bonus, etc or other adjustment that has a set amount
// and not hourly."
//
// A reimbursement has NO job behind it, so the fallback above has nothing to
// fall back to: without an amount of its own the row resolves to the worker's
// hourly rate times zero hours and pays nothing. The amount IS the line. So
// this file sends `pay_type: 'fixed'` and `pay_amount`, and that is the whole
// of the exception — it still computes, sums and rounds nothing. What the row
// is worth is decided by paystubCalc.computeFigures on the server, which has
// added a fixed entry's amount since long before this existed.
//
// Three invariants hold it, each pinned in sheetLines.test.js:
//
//   hours is 0        computeFigures adds `hours` to the week whatever the
//                     pay type, so an amount line with hours would put hours
//                     nobody worked onto a paystub.
//   job_id is null    validateLines refuses a second line for the same (job,
//                     day) — a reimbursement on a day already worked would be
//                     a 400 — and a job's pay_type sitting behind the entry's
//                     is #202's exact shape.
//   pay_amount > 0    resolveEntryPay keys off it. At or below zero the row
//                     falls through to the HOURLY branch and pays hours x the
//                     worker's rate, silently. newLineError refuses it.

import { addDaysISO } from '../../../utils/scheduleDates';
import { entriesForPut } from '../lib/format';

// ── What an amount line IS ────────────────────────────────────────────────
//
// Owner, 2026-09-16: "set some default useful settings like Reimbursement so
// they clearly get marked to make things easier for the employee accountant.
// Reimbursements are not taxed to the employee, etc and other (asking for
// specific description)."
//
// THE SERVER OWNS THIS LIST. server/utils/payCategories.js is the original;
// this is the copy the client needs because it cannot import from server/, and
// payCategory.test.js reads both and compares them key for key, label for
// label, order, needsSpecific — and since 2026-09-16 the PROSE too: note, hint
// and placeholder. That last one matters most for `note`, which is the
// sentence this screen actually renders and the one string here that makes a
// claim about somebody's money; it was comparable in neither direction until a
// review pointed out a one-sided edit would pass everything. Same shape,
// same reason, as the discountMath.js mirror: a screen that hardcodes its own
// rules is how it ends up offering a button the server answers 400 to — and
// hoursModel.validateLines refuses an unknown category with a sentence.
//
// IT CHANGES NO FIGURE (CLAUDE.md rule 4). A category is a label. Nothing in
// utils/paystubCalc.js reads it and the same line pays what it always paid.
// The reimbursement note is guidance for the person doing the books, printed
// where they are making the decision — this application computes no tax at
// all, and the note must never be written as though it did.
export const PAY_CATEGORIES = [
  {
    key: 'reimbursement',
    label: 'Reimbursement',
    hint: 'Money the worker already spent, paid back. Not wages.',
    note: 'Reimbursements are not taxed to the employee.',
    placeholder: 'Fuel — Home Depot run, 12 Sep',
  },
  {
    key: 'bonus',
    label: 'Bonus',
    hint: 'Extra pay for work done. Wages, like any other pay.',
    placeholder: 'Finished the Elm St job a day early',
  },
  {
    key: 'allowance',
    label: 'Allowance',
    hint: 'A standing payment — tools, phone, boots.',
    placeholder: 'Monthly tool allowance',
  },
  {
    key: 'correction',
    label: 'Correction to an earlier week',
    hint: 'Pay missed on a sheet that was already approved.',
    placeholder: 'Missed 2h on the week of 1 Sep',
  },
  {
    key: 'other',
    label: 'Other',
    hint: 'Say exactly what this is — "Other" on a paystub tells nobody anything.',
    needsSpecific: true,
    placeholder: '',
  },
];

/** The whole entry, or undefined. */
export function payCategory(key) {
  return PAY_CATEGORIES.find((c) => c.key === key);
}

/** What to call it on a screen. Falls back to the key so nothing renders blank. */
export function payCategoryLabel(key) {
  const found = payCategory(key);
  return found ? found.label : (key || null);
}

/**
 * Descriptions that say nothing, refused on the one category that asks for a
 * specific one.
 *
 * "Other" plus a description reading "other" is exactly the row this feature
 * exists to stop being written — the bookkeeper is left with a $200 line
 * called "misc" and no way to find out what it was. Deliberately a SHORT list
 * of the words that are literally the category, not a quality judgement: a
 * rule that guesses at whether a sentence is informative enough would refuse
 * real ones, and a refusal on a payroll screen has to be obviously fair.
 */
const SAYS_NOTHING = ['other', 'misc', 'miscellaneous', 'n/a', 'na', '-', 'adjustment', 'payment'];


const isDateOnly = (iso) => typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso);

/**
 * The days a line may be dated, inclusive of both ends.
 *
 * A <select> of the sheet's own week rather than a free date input: a line
 * dated outside the week is stored happily by the server and then belongs to
 * no sheet anybody reviews. Capped at 14 so a corrupt end_date cannot spin.
 */
export function weekDates(startDate, endDate) {
  if (!isDateOnly(startDate)) return [];
  const end = isDateOnly(endDate) && endDate >= startDate ? endDate : startDate;
  const out = [];
  let cur = startDate;
  while (cur <= end && out.length < 14) {
    out.push(cur);
    cur = addDaysISO(cur, 1);
  }
  return out;
}

/** Which of the two kinds of line a draft is. Anything unnamed is hours. */
export function draftKind(draft) {
  return draft && draft.kind === 'amount' ? 'amount' : 'hours';
}

/**
 * One id-less entry — what applyLineEdits INSERTS.
 *
 * `id` is absent, not null: the server's test is `e.id != null`, so either
 * would insert, but an absent key says the line has no identity yet rather
 * than that its identity is unknown.
 *
 * Two shapes, and the header of this file says why each field is what it is.
 * The default is the hours line, so no existing caller changes.
 */
export function newLinePayload(draft) {
  const notes = String((draft && draft.notes) || '').trim();
  if (draftKind(draft) === 'amount') {
    return {
      // NOT the job — see the header. A reimbursement is named by what it is.
      job_id: null,
      date: (draft && draft.date) || null,
      // Zero, always: the week's hours are hours somebody worked.
      hours: 0,
      custom_description: String((draft && draft.custom_description) || '').trim() || null,
      pay_type: 'fixed',
      // What the line IS, beside what it is called. Both, not one: the
      // description is free text a person writes and the category is the thing
      // a bookkeeper can filter on.
      pay_category: (draft && draft.pay_category) || null,
      // A number, not the string the input handed over. It lands in a DECIMAL
      // column, and a payload that relies on parseFloat downstream to rescue
      // it is one nobody can reason about.
      pay_amount: Number(String((draft && draft.pay_amount) || '').trim()),
      notes: notes || null,
    };
  }
  return {
    job_id: Number(draft && draft.job_id),
    date: (draft && draft.date) || null,
    hours: Number(draft && draft.hours),
    notes: notes || null,
  };
}

/** The FULL array for the PUT, with one line added at the end. */
export function entriesWithNewLine(entries, draft) {
  return [...entriesForPut(entries), newLinePayload(draft)];
}

/**
 * The FULL array for the PUT, with one line gone. Omission IS the delete
 * (hoursModel.js:224) — there is nothing else to send.
 */
export function entriesWithout(entries, id) {
  return entriesForPut((entries || []).filter((e) => String(e.id) !== String(id)));
}

/**
 * Why this draft cannot be saved, in a sentence, or null.
 *
 * The duplicate rule is hoursModel.validateLines:245-251 — the same job on the
 * same DAY twice — restated where a person can still fix it. The archived rule
 * (:242) is enforced by not offering archived jobs in the picker.
 */
export function newLineError(draft, entries) {
  const d = draft || {};

  // ── The amount line ────────────────────────────────────────────────────
  //
  // No job, so no archived-job rule and no same-job-same-day rule apply to it
  // (validateLines skips entries with no job_id). Two things must be true,
  // and the second is the one that matters: resolveEntryPay keys off
  // `pay_amount > 0`, so a 'fixed' line at or below zero falls through to the
  // HOURLY branch of computeFigures and pays hours x the worker's rate. On a
  // zero-hours line that is $0 — a row that looks recorded, is on the
  // paystub, and pays nothing. Refusing it here is the only place a person
  // can still fix it.
  if (draftKind(d) === 'amount') {
    if (!isDateOnly(d.date)) return 'Pick the day this belongs to.';
    // Required on a NEW line, and only on a new one. hoursModel.validateLines
    // allows null, because every amount line written before migration 0008 has
    // one and editing such a line's hours must not be refused. This is the
    // half that stops new uncategorised lines being created — without it most
    // lines stay null and the bookkeeper is no better off than before.
    const category = payCategory(d.pay_category);
    if (!category) return 'Say what kind of payment this is.';
    const what = String(d.custom_description || '').trim();
    if (!what) {
      return 'Say what this is for — a line with no job is named by its description on the paystub.';
    }
    if (category.needsSpecific && SAYS_NOTHING.includes(what.toLowerCase())) {
      return `"${what}" does not say what this is. "Other" is the category; the description is where `
        + 'the person doing the books finds out what they are paying for.';
    }
    const amount = Number(String(d.pay_amount == null ? '' : d.pay_amount).trim());
    if (!Number.isFinite(amount) || amount <= 0) {
      return 'The amount must be a number greater than 0. A line with no amount is paid by the hour instead, which for no hours is nothing.';
    }
    return null;
  }

  if (!d.job_id) return 'Pick the job these hours were worked on.';
  if (!isDateOnly(d.date)) return 'Pick the day the work happened.';
  const hours = Number(d.hours);
  if (!Number.isFinite(hours) || hours <= 0) return 'Hours must be a number greater than 0.';
  const clash = (entries || []).some((e) => e.job_id != null
    && String(e.job_id) === String(d.job_id) && e.date === d.date);
  if (clash) return 'That job already has a line on that day. Edit that line instead of adding a second one.';
  return null;
}

/**
 * The picker, week jobs first.
 *
 * `sheet.week_jobs` is derived from the worker's own time entries, so it is
 * the set that cannot disagree with the sheet builder (see SheetDetail's
 * WeekJobs comment). But the line this feature exists for is a job with NO
 * clock time — a forgotten clock-in is invisible to that list by definition —
 * so the rest of the job list is offered underneath it.
 *
 * Archived jobs are dropped: validateLines refuses them with a 400.
 */
export function jobChoices(weekJobs, allJobs) {
  const week = (weekJobs || [])
    .filter((j) => j && j.id != null && j.status !== 'archived')
    .map((j) => ({ id: j.id, label: j.title || `Job #${j.id}` }));
  const seen = new Set(week.map((j) => String(j.id)));
  const rest = (allJobs || [])
    .filter((j) => j && j.id != null && j.status !== 'archived' && !seen.has(String(j.id)))
    .map((j) => ({ id: j.id, label: [j.title || `Job #${j.id}`, j.client_name].filter(Boolean).join(' · ') }));
  return { week, rest };
}
